// v53 — the rail's icon set, ported verbatim from Prototype-Chunks/sandbox.js.
//
// Kept SEPARATE from components/Icon.tsx on purpose. Our Icon set is drawn per
// AREA — one glyph for a whole section of the product — and the prototype's is
// drawn per ITEM. That difference is a lot of why the two rails read so
// unalike: "Find tenders" and "Find labour" share an area in our model and get
// the same glyph, while the prototype gives them a document and a magnifier.
//
// Same geometry as the source: 24-unit viewBox, 16px rendered, stroke-width 2,
// round joins, no fill. Changing any of those makes the set stop matching the
// rest of the prototype's chrome, so they are not props.

import type { RailIcon } from "./railData";

const PATHS: Record<RailIcon, React.ReactNode> = {
  today: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,

  inbox: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,

  find: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,

  projects: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,

  compliance: <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" />,

  payments: <><circle cx="8.5" cy="8.5" r="5" /><path d="M14 6.5a5 5 0 0 1 0 11" /></>,

  insights: <><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></>,

  community: <>
    <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="3.5" />
    <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
  </>,

  settings: <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.3 1a7 7 0 0 0-2-1.2L14 2h-4l-.6 2.4a7 7 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.6A7 7 0 0 0 5 12a7 7 0 0 0 .1 1.2l-2 1.6 2 3.4 2.3-1a7 7 0 0 0 2 1.2L10 22h4l.6-2.4a7 7 0 0 0 2-1.2l2.3 1 2-3.4-2-1.6A7 7 0 0 0 19 12z" />
  </>,

  tenders: <>
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
    <path d="M9 14h6M9 17h4" />
  </>,

  design: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,

  deals: <><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></>,

  bookings: <>
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <path d="M3 9h18M8 2v4M16 2v4M9 15l2 2 4-4" />
  </>,

  siteops: <>
    <path d="M3 21h18" />
    <path d="M5 21V8l7-4 7 4v13" />
    <path d="M9 12h.01M9 16h.01M15 12h.01M15 16h.01" />
  </>,

  calendar: <>
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <path d="M3 9h18M8 2v4M16 2v4" />
  </>,
};

export function RailIconGlyph({ name, size = 16 }: { name: RailIcon; size?: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}
